import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import store from 'store';

import 'store/configureRTK';
import type { InsightCardItemType } from 'api/endpoints/review-insights';
import type { InsightValueType } from 'api/endpoints/review-insights/shared.types';

type CreateCardPropsType = {
  type: InsightValueType;
} & (
  | {
      type: 'topic_summary';
      title: string;
      body: string;
    }
  | {
      type: Omit<InsightValueType, 'topic_summary'>;
      title: any;
      body: any;
    }
);

type ReviewInsightsView = {
  aspectsOffset: number;
  isHiddenListExpanded: boolean;
  createCardProps?: CreateCardPropsType;
  rivalId?: number;
  insightsCardList?: InsightCardItemType[];
  createCardModalStep: number;
  createCardModalId: number;
};

const initialState: ReviewInsightsView = {
  aspectsOffset: 0,
  isHiddenListExpanded: false,
  createCardProps: undefined,
  rivalId: undefined,
  insightsCardList: undefined,
  createCardModalStep: 0,
  createCardModalId: 0,
};

const reviewInsightsView = createSlice({
  name: 'reviewInsightsView',
  initialState,
  reducers: {
    setAspectsOffset(state, action: PayloadAction<number>) {
      state.aspectsOffset = action.payload;
    },
    setIsHiddenListExpanded(state, action: PayloadAction<boolean>) {
      state.isHiddenListExpanded = action.payload;
    },
    setCreateCardProps(
      state,
      action: PayloadAction<CreateCardPropsType | undefined>,
    ) {
      state.createCardProps = action.payload;
    },
    setRivalId(state, action: PayloadAction<number | undefined>) {
      state.rivalId = action.payload;
    },
    setInsightsCardList(
      state,
      action: PayloadAction<InsightCardItemType[] | undefined>,
    ) {
      state.insightsCardList = action.payload;
    },
    setCreateCardModalStep(state, action: PayloadAction<number>) {
      state.createCardModalStep = action.payload;
    },
    setCreateCardModalId(state, action: PayloadAction<number>) {
      state.createCardModalId = action.payload;
    },
  },
});

store.addModel({
  name: 'reviewInsightsView',
  state: reviewInsightsView.getInitialState(),
  reducers: {},
  baseReducer: reviewInsightsView.reducer,
});

const selectSelf = createSelector(
  [
    (state: { reviewInsightsView: ReviewInsightsView }) =>
      state.reviewInsightsView,
  ],
  (reviewInsightsView) => reviewInsightsView,
);

const selectAspectsOffset = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.aspectsOffset,
);

const selectIsHiddenListExpanded = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.isHiddenListExpanded,
);

const selectCreateCardType = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.createCardProps?.type,
);

const selectCreateCardProps = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.createCardProps,
);

const selectRivalId = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.rivalId,
);

const selectInsightsCardList = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.insightsCardList,
);

const selectCreateCardModalStep = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.createCardModalStep,
);

const selectCreateCardModalId = createSelector(
  [selectSelf],
  (reviewInsightsView) => reviewInsightsView.createCardModalId,
);

export const useAspectsOffset = () =>
  useSelector<{ reviewInsightsView: ReviewInsightsView }, number>(
    selectAspectsOffset,
  );

export const useIsHiddenListExpanded = () =>
  useSelector<{ reviewInsightsView: ReviewInsightsView }, boolean>(
    selectIsHiddenListExpanded,
  );

export const useCreateCardType = () =>
  useSelector<
    { reviewInsightsView: ReviewInsightsView },
    InsightValueType | undefined
  >(selectCreateCardType);

export const useCreateCardProps = () =>
  useSelector<
    { reviewInsightsView: ReviewInsightsView },
    CreateCardPropsType | undefined
  >(selectCreateCardProps);

export const useRivalId = () =>
  useSelector<{ reviewInsightsView: ReviewInsightsView }, number | undefined>(
    selectRivalId,
  );

export const useInsightsCardList = () =>
  useSelector<
    { reviewInsightsView: ReviewInsightsView },
    InsightCardItemType[] | undefined
  >(selectInsightsCardList);

export const useCreateCardModalStep = () =>
  useSelector<{ reviewInsightsView: ReviewInsightsView }, number>(
    selectCreateCardModalStep,
  );

export const useCreateCardModalId = () =>
  useSelector<{ reviewInsightsView: ReviewInsightsView }, number>(
    selectCreateCardModalId,
  );

export const setAspectsOffset = (offset: number) =>
  store.dispatch(reviewInsightsView.actions.setAspectsOffset(offset));

export const setIsHiddenListExpanded = (isHiddenListExpanded: boolean) =>
  store.dispatch(
    reviewInsightsView.actions.setIsHiddenListExpanded(isHiddenListExpanded),
  );

export const setCreateCardProps = (
  createCardType: CreateCardPropsType | undefined,
) =>
  store.dispatch(reviewInsightsView.actions.setCreateCardProps(createCardType));

export const setRivalId = (rivalId: number | undefined) =>
  store.dispatch(reviewInsightsView.actions.setRivalId(rivalId));

export const setInsightsCardList = (
  insightsCardList: InsightCardItemType[] | undefined,
) =>
  store.dispatch(
    reviewInsightsView.actions.setInsightsCardList(insightsCardList),
  );

export const setCreateCardModalStep = (step: number) =>
  store.dispatch(reviewInsightsView.actions.setCreateCardModalStep(step));

export const setCreateCardModalId = (id: number) =>
  store.dispatch(reviewInsightsView.actions.setCreateCardModalId(id));

export default reviewInsightsView;
